import { API_CONFIG, NGROK_HEADERS } from './config';
import { parseValidationErrors } from '@/utils/errorHandling';
class ApiClient {
    constructor(baseURL) {
        Object.defineProperty(this, "baseURL", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getAccessToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onLogout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "validationErrorHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onForbidden", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.baseURL = baseURL;
    }
    getBaseUrl() { return this.baseURL; }
    // Set auth token getter and logout handler
    setAuthHandlers(getAccessToken, onLogout) {
        this.getAccessToken = getAccessToken;
        this.onLogout = onLogout;
    }
    // Set global validation error handler for 422 responses
    setValidationErrorHandler(handler) {
        this.validationErrorHandler = handler;
    }
    // Set global forbidden handler for 403 responses (permission changes)
    setForbiddenHandler(handler) {
        this.onForbidden = handler;
    }
    // Request interceptor
    async prepareRequest(endpoint, config = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const headers = {
            'Accept': 'application/json',
            ...NGROK_HEADERS,
            ...config.headers,
        };
        // Only set Content-Type to JSON if we're not sending FormData
        if (!(config.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        // Add authorization header if auth is required
        if (config.requiresAuth !== false && this.getAccessToken) {
            try {
                // Add timeout to prevent hanging - Auth0 can sometimes hang indefinitely
                let timeoutId;
                const timeoutPromise = new Promise((_, reject) => {
                    timeoutId = setTimeout(() => reject(new Error('getAccessToken timeout after 5s')), 5000);
                });
                let accessToken;
                try {
                    accessToken = await Promise.race([
                        this.getAccessToken(),
                        timeoutPromise
                    ]);
                }
                finally {
                    if (timeoutId !== undefined) {
                        clearTimeout(timeoutId);
                    }
                }
                if (accessToken) {
                    headers['Authorization'] = `Bearer ${accessToken}`;
                }
            }
            catch (error) {
                console.error('Error getting access token:', error);
                // Continue without token - let the server return 401 if needed
            }
        }
        const requestInit = {
            method: config.method || 'GET',
            headers,
            mode: 'cors',
            credentials: 'omit',
        };
        // Add body for non-GET requests
        if (config.body && config.method !== 'GET') {
            if (config.body instanceof FormData) {
                // For FormData, use as-is and let browser set Content-Type with boundary
                requestInit.body = config.body;
            }
            else {
                // For other body types, stringify if needed
                requestInit.body = typeof config.body === 'string'
                    ? config.body
                    : JSON.stringify(config.body);
            }
        }
        // Attach abort signal if provided
        if (config.signal) {
            requestInit.signal = config.signal;
        }
        return [url, requestInit];
    }
    // Response interceptor
    async processResponse(response) {
        const result = {
            status: response.status,
            statusText: response.statusText,
        };
        // Capture ETag header if present
        const etag = response.headers.get('etag') || response.headers.get('ETag');
        if (etag) {
            result.etag = etag;
        }
        // Handle 401 - Auto logout
        if (response.status === 401) {
            if (this.onLogout) {
                this.onLogout();
            }
            result.error = 'Unauthorized - please login again';
            return result;
        }
        // Handle 403 - Permission denied (user's roles/capabilities changed)
        if (response.status === 403) {
            if (this.onForbidden) {
                this.onForbidden();
            }
            result.error = 'You do not have permission to perform this action';
            return result;
        }
        // Handle 404
        if (response.status === 404) {
            result.error = 'Resource not found';
            return result;
        }
        if (!response.ok) {
            const contentType = response.headers.get('content-type');
            // For validation errors (422) and other JSON error responses
            if (contentType && contentType.includes('application/json')) {
                try {
                    const errorJson = await response.json();
                    // If the server returns an array (e.g., list of validation errors), serialize it
                    if (Array.isArray(errorJson)) {
                        result.error = JSON.stringify(errorJson);
                        result.status = response.status;
                        // Global 422 handling - Business Rule Errors
                        if (response.status === 422 && this.validationErrorHandler) {
                            const validationErrors = parseValidationErrors({ error: result.error });
                            if (validationErrors.length > 0) {
                                this.validationErrorHandler(validationErrors);
                            }
                        }
                        return result;
                    }
                    // Otherwise attach properties and preserve status
                    Object.assign(result, errorJson);
                    // Ensure there's at least an error string for downstream handlers
                    if (!result.error) {
                        result.error = typeof errorJson.message === 'string'
                            ? errorJson.message
                            : `HTTP ${response.status}: ${response.statusText}`;
                    }
                    result.status = response.status;
                    // Global 422 handling - Business Rule Errors
                    if (response.status === 422 && this.validationErrorHandler) {
                        const validationErrors = parseValidationErrors(result);
                        if (validationErrors.length > 0) {
                            this.validationErrorHandler(validationErrors);
                        }
                    }
                    return result;
                }
                catch (e) {
                    // If JSON parsing fails, fall back to text
                    const errorText = await response.text();
                    result.error = errorText || `HTTP ${response.status}: ${response.statusText}`;
                    return result;
                }
            }
            else {
                // Non-JSON error response
                const errorText = await response.text();
                result.error = errorText || `HTTP ${response.status}: ${response.statusText}`;
                return result;
            }
        }
        // For 204/205 No Content, don't attempt to parse a body
        if (response.status === 204 || response.status === 205) {
            return result;
        }
        // Handle 304 Not Modified - signal to use cached data
        if (response.status === 304) {
            // Don't set result.data - this signals to the cache hook to keep using cached data
            return result;
        }
        // Parse successful response
        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                result.data = await response.json();
            }
            else {
                // Non-JSON responses fallback to raw text
                result.data = await response.text();
            }
        }
        catch (error) {
            console.error('Error parsing response:', error);
            result.error = 'Failed to parse response';
        }
        return result;
    }
    // Main request method
    async request(endpoint, config = {}) {
        try {
            const [url, requestInit] = await this.prepareRequest(endpoint, config);
            const response = await fetch(url, requestInit);
            return await this.processResponse(response);
        }
        catch (error) {
            // Suppress noisy AbortError logs from canceled searches
            if (error && error.name === 'AbortError') {
                return {
                    status: 0,
                    statusText: 'Aborted',
                    error: 'Request aborted',
                };
            }
            console.error('Network error:', error);
            if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                return {
                    status: 0,
                    statusText: 'CORS Error',
                    error: 'CORS error: Please check your API server configuration.',
                };
            }
            return {
                status: 0,
                statusText: 'Network Error',
                error: 'Network request failed. Please check your connection.',
            };
        }
    }
    // Convenience methods
    async get(endpoint, config = {}) {
        return this.request(endpoint, { ...config, method: 'GET' });
    }
    async post(endpoint, body, config = {}) {
        return this.request(endpoint, { ...config, method: 'POST', body });
    }
    async put(endpoint, body, config = {}) {
        return this.request(endpoint, { ...config, method: 'PUT', body });
    }
    async delete(endpoint, config = {}) {
        return this.request(endpoint, { ...config, method: 'DELETE' });
    }
    async patch(endpoint, body, config = {}) {
        return this.request(endpoint, { ...config, method: 'PATCH', body });
    }
}
// Create and export the API client instance
export const apiClient = new ApiClient(API_CONFIG.BASE_URL);
